import { createContext, useContext, useState, ReactNode, useEffect, useRef, useCallback } from 'react';
import { supabase } from '../utils/supabase/client';
import { projectId, publicAnonKey } from '../utils/supabase/info';
import { loadUserSettings, clearSettingsCache } from '../data/settings';
import { makeAuthenticatedFetch } from '../utils/api/auth';
import { getErrorMessage, getErrorName } from '../utils/errors';
import { identifyUser, trackEvent } from '../app-core/analytics/posthog';
import { setUser as setSentryUser } from '../app-core/analytics/sentry';
import { trackAuthSessionRestored, trackAuthSignOut } from '../app-core/analytics/posthog';
import { getSessionConfig } from '../utils/auth/sessionConfig';
import { calculateIdleStatus } from '../utils/auth/sessionTimeout';

interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  isAdmin: boolean;
}

interface UserContextType {
  user: User | null;
  accessToken: string | null;
  login: (email: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  isLoading: boolean;
  /** Set active run flag (called by run orchestration) */
  setActiveRun: (active: boolean) => void;
  /** Whether an active run is in progress */
  activeRunInProgress: boolean;
}

const UserContext = createContext<UserContextType | undefined>(undefined);

const debugLog = (...args: unknown[]) => {
  if (import.meta.env.DEV) console.log(...args);
};

const debugWarn = (...args: unknown[]) => {
  if (import.meta.env.DEV) console.warn(...args);
};

const debugError = (...args: unknown[]) => {
  if (import.meta.env.DEV) console.error(...args);
};

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  // Start as false - don't block render waiting for session check
  const [isLoading, setIsLoading] = useState(false);
  const isProcessingAuthRef = useRef(false);
  const currentUserIdRef = useRef<string | null>(null);

  // Session management state (Epic 4.1)
  const [activeRunInProgress, setActiveRunInProgress] = useState(false);
  const sessionStartTimeRef = useRef<number>(Date.now());
  const lastActivityTimeRef = useRef<number>(Date.now());

  /** Set active run flag — called by run orchestration */
  const setActiveRun = useCallback((active: boolean) => {
    setActiveRunInProgress(active);
    if (import.meta.env.DEV) {
      debugLog(`[Session] Active run: ${active}`);
    }
  }, []);

  // Verification log - confirms UserProvider mounted
  useEffect(() => {
    debugLog("✅ UserProvider mounted");
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) {
          debugError('Session check error:', error);
          
          // Track failed session restoration telemetry (Epic 3.10)
          trackAuthSessionRestored(false);
          
          setIsLoading(false);
          return;
        }

        if (session?.user) {
          setAccessToken(session.access_token);
          localStorage.setItem('loop_supabase_session', JSON.stringify(session));
          // Fetch user profile from our profiles table
          const profile = await fetchUserProfile(session.user.id);
          if (profile) {
            setUser(profile);
            currentUserIdRef.current = profile.id;
            // Load user settings
            await loadUserSettings(session.user.id, session.access_token);
            
            // Track successful session restoration telemetry (Epic 3.10)
            trackAuthSessionRestored(true);
          } else {
            // Track failed session restoration telemetry (Epic 3.10)
            trackAuthSessionRestored(false);
          }
        } else {
          setAccessToken(null);
          localStorage.removeItem('loop_supabase_session');
          // No session to restore - don't track (user hasn't logged in yet)
        }
      } catch (error) {
        debugError('Session check exception:', error);
        
        // Track failed session restoration telemetry (Epic 3.10)
        trackAuthSessionRestored(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkSession();

    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event, session) => {
      debugLog('🔐 Auth state change:', event, 'User ID:', session?.user?.id);
      
      // Prevent processing if already processing
      if (isProcessingAuthRef.current) {
        debugLog('⏭️ Skipping auth state change - already processing');
        return;
      }
      
      if (event === 'TOKEN_REFRESHED' && session?.user) {
        debugLog('🔄 Token refreshed, updating state');
        setAccessToken(session.access_token);
        localStorage.setItem('loop_supabase_session', JSON.stringify(session));
      } else if (event === 'SIGNED_IN' && session?.user) {
        setAccessToken(session.access_token);
        localStorage.setItem('loop_supabase_session', JSON.stringify(session));
        // Skip if we already have this user
        if (currentUserIdRef.current === session.user.id) {
          debugLog('⏭️ Skipping auth state change - user already set');
          return;
        }
        
        isProcessingAuthRef.current = true;
        const profile = await fetchUserProfile(session.user.id);
        if (profile) {
          setUser(profile);
          currentUserIdRef.current = profile.id;
          // Load user settings
          await loadUserSettings(session.user.id, session.access_token);
        }
        isProcessingAuthRef.current = false;
      } else if (event === 'SIGNED_OUT') {
        debugLog('👋 User signed out');
        setUser(null);
        currentUserIdRef.current = null;
        setAccessToken(null);
        localStorage.removeItem('loop_supabase_session');
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []); // Keep empty dependency array to avoid re-registering listener

  // Activity-aware session timeout (Epic 4.1 — replaces SEC-039-03 30-min timeout)
  useEffect(() => {
    if (!user) return; // Only track activity when user is logged in

    // Load configuration with security-enforced boundaries
    const config = getSessionConfig();
    const idleTimeoutMs = config.idleTimeoutMs;

    // Reset session start time on login
    sessionStartTimeRef.current = Date.now();
    lastActivityTimeRef.current = Date.now();
    
    let idleCheckInterval: NodeJS.Timeout | null = null;
    
    const updateActivity = () => {
      lastActivityTimeRef.current = Date.now();
    };
    
    // Track user activity (same events as before + route changes via ref)
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart'];
    events.forEach(event => {
      window.addEventListener(event, updateActivity);
    });
    
    // Check idle timeout every 60 seconds (same interval as before)
    idleCheckInterval = setInterval(() => {
      const result = calculateIdleStatus({
        lastActivityTime: lastActivityTimeRef.current,
        activeRunInProgress,
        idleTimeoutMs,
        sessionStartTime: sessionStartTimeRef.current,
      });

      if (result.shouldLogout) {
        if (result.reason === 'max_session_exceeded') {
          debugLog('[Auth] Max session duration exceeded (8 hours) - logging out');
          trackEvent('security.max_session_enforced', {
            sessionDurationMs: result.sessionDurationMs,
            activeRunInProgress,
          });
        } else {
          debugLog(`[Auth] Idle timeout exceeded (${Math.round(idleTimeoutMs / 60000)} min) - logging out`);
          trackEvent('auth.session_idle_timeout', {
            idleDurationMs: result.idleDurationMs,
            configuredTimeoutMs: idleTimeoutMs,
            activeRunInProgress: false,
          });
        }
        supabase.auth.signOut();
        if (idleCheckInterval) clearInterval(idleCheckInterval);
      }
    }, 60 * 1000);
    
    return () => {
      events.forEach(event => {
        window.removeEventListener(event, updateActivity);
      });
      if (idleCheckInterval) {
        clearInterval(idleCheckInterval);
      }
    };
  }, [user, activeRunInProgress]);

  // Telemetry: Identify user with PostHog and Sentry (Fixes CR-027-01)
  useEffect(() => {
    if (user && user.id) {
      // Identify user with PostHog (async, won't block UI)
      identifyUser(user.id, user.isAdmin || false).catch(err => {
        debugWarn('Failed to identify user with PostHog:', err);
      });
      
      // Set Sentry user context
      setSentryUser(user.id, user.isAdmin || false).catch(err => {
        debugWarn('Failed to set Sentry user context:', err);
      });
      
      debugLog('📊 Telemetry user identified:', user.id.substring(0, 8) + '...');
    } else if (user === null) {
      // User signed out - clear telemetry context
      setSentryUser(null).catch(err => {
        debugWarn('Failed to clear Sentry user context:', err);
      });
      
      debugLog('📊 Telemetry user context cleared');
    }
  }, [user]);

  const fetchUserProfile = async (userId: string): Promise<User | null> => {
    try {
      debugLog('🔍 Fetching profile for user:', userId);
      
      const profileUrl = `https://${projectId}.supabase.co/functions/v1/make-server-7f15104b/auth/profile`;
      debugLog('Fetching from:', profileUrl);
      
      const response = await makeAuthenticatedFetch(profileUrl);
      debugLog('Profile response received, status:', response.status);

      if (!response.ok) {
        debugError('Profile fetch failed with status:', response.status);
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        debugError('Profile fetch error:', errorData);
        
        // Return a default profile based on user metadata from session
        debugLog('⚠️ Using fallback profile from session');
        return {
          id: userId,
          email: '',
          firstName: '',
          lastName: '',
          isAdmin: false,
        };
      }

      const { profile } = await response.json();
      debugLog('✅ Profile fetched successfully:', profile);
      
      return {
        id: profile.id,
        email: profile.email,
        firstName: profile.firstName,
        lastName: profile.lastName,
        isAdmin: profile.isAdmin === true,
      };
    } catch (error: unknown) {
      // Check if this is a network error (server not running)
      const errorMessage = getErrorMessage(error);
      const errorName = getErrorName(error);
      if (errorMessage === 'Failed to fetch' || errorName === 'TypeError') {
        debugWarn('⚠️ Backend server not reachable - using fallback profile');
        debugLog('This is expected in development if the Supabase Edge Function is not deployed');
      } else if (errorMessage?.includes('Session expired')) {
        debugError('🔐 Session expired during profile fetch');
        return null;
      } else {
        debugError('Profile fetch exception:', error);
      }
      
      // Return a default profile to allow the app to continue
      return {
        id: userId,
        email: '',
        firstName: '',
        lastName: '',
        isAdmin: false,
      };
    }
  };

  const login = async (email: string, password: string): Promise<boolean> => {
    try {
      debugLog('Login attempt for:', email);
      
      // Quick login if email contains just @
      if (email === '@' || email.includes('@') && password === '') {
        // Use demo account credentials
        email = 'chris@loop.com';
        password = 'loopyloop';
      }

      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        debugError('Login error:', error.message, error);
        return false;
      }

      if (!data.user) {
        debugError('Login succeeded but no user returned');
        return false;
      }

      debugLog('Auth login successful, fetching profile for:', data.user.id);
      
      // Store session immediately so getCurrentAccessToken can use it
      setAccessToken(data.session.access_token);
      localStorage.setItem('loop_supabase_session', JSON.stringify(data.session));
      
      // Small delay to ensure Supabase client updates internal session state
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Fetch profile with a longer timeout to accommodate retries
      // makeAuthenticatedFetch has 10s timeout per request + up to 3 retries
      const profileFetchStart = Date.now();
      
      try {
        const profile = await Promise.race([
          fetchUserProfile(data.user.id),
          new Promise<null>((_, reject) => 
            setTimeout(() => reject(new Error('Profile fetch timeout')), 15000) // 15 second timeout
          )
        ]);
        
        const fetchDuration = Date.now() - profileFetchStart;
        debugLog(`✅ Profile fetch completed in ${fetchDuration}ms`);
        
        if (!profile) {
          debugError('Profile not found in database for user:', data.user.id);
          // Use fallback profile instead of failing login
          const fallbackProfile: User = {
            id: data.user.id,
            email: data.user.email || email,
            firstName: '',
            lastName: '',
            isAdmin: false,
          };
          debugLog('Using fallback profile for login');
          setUser(fallbackProfile);
          return true;
        }

        debugLog('Login complete, setting user:', profile);
        setUser(profile);
        
        // Load user settings from backend (non-blocking)
        loadUserSettings(data.user.id, data.session.access_token).catch(err => {
          debugWarn('Failed to load user settings:', err);
        });
        
        return true;
      } catch (err) {
        const fetchDuration = Date.now() - profileFetchStart;
        debugWarn(`⏱️ Profile fetch timeout after ${fetchDuration}ms - using fallback profile`);
        
        // Use fallback profile when fetch times out
        // Session already stored above
        const fallbackProfile: User = {
          id: data.user.id,
          email: data.user.email || email,
          firstName: '',
          lastName: '',
          isAdmin: false,
        };
        setUser(fallbackProfile);
        return true;
      }
    } catch (error) {
      debugError('❌ Login error:', error);
      return false;
    }
  };

  const logout = async () => {
    try {
      debugLog('🔓 Starting logout process...');
      await supabase.auth.signOut();
      debugLog('✅ Supabase signOut successful');
      
      setUser(null);
      setAccessToken(null);
      currentUserIdRef.current = null;
      localStorage.removeItem('loop_supabase_session');
      clearSettingsCache();
      
      debugLog('✅ Logout complete - user state cleared');
    } catch (error) {
      debugError('❌ Logout error:', error);
      // Even if logout fails, clear local state
      setUser(null);
      setAccessToken(null);
      currentUserIdRef.current = null;
      localStorage.removeItem('loop_supabase_session');
      clearSettingsCache();
    }
  };

  return (
    <UserContext.Provider value={{ user, accessToken, login, logout, isLoading, setActiveRun, activeRunInProgress }}>
      {children}
    </UserContext.Provider>
  );
}

export function useUser() {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}